import React from 'react';
import { Content, Header, Item, Root, Trigger } from '@radix-ui/react-accordion';
import { keyframes, styled } from '@components-shared/src/stitches';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import { ClosedTriggerContent, OpenTriggerContent } from './SaleDetails';

//SaleDetails Shared Styles
export const SaleDetailsRoot = styled(Root, {
  lift: 'tertiary',
  borderRadius: '0 0 8px 8px',
  width: '100%',
});

const slideDown = keyframes({
  from: { height: 0 },
  to: { height: 'var(--radix-accordion-content-height)' },
});

const slideUp = keyframes({
  from: { height: 'var(--radix-accordion-content-height)' },
  to: { height: 0 },
});

export const SaleDetailsContent = styled(Content, {
  '&[data-state="open"]': {
    animation: `${slideDown} 300ms ease-out`,
  },
  '&[data-state="closed"]': {
    animation: `${slideUp} 300ms ease-out`,
    opacity: '0',
    transition: 'opacity 0.5s ease',
  },
});

export const SaleDetailsHeader = styled(Header, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$white',
});

export const SaleDetailsTrigger = styled(Trigger, {
  display: 'flex',
  height: '100%',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  base: 'M',
  color: '$base500',
  backgroundColor: '$white',
  fontWeight: 500,
  gap: 8,
  outline: 'none',
  padding: '18px 0 16px 0',

  p: {
    whiteSpace: 'nowrap',
  },

  svg: {
    color: '$base500',
  },
  '&:hover': {
    color: '$base800',
    svg: {
      color: '$base800',
    },
  },

  '&:active': {
    color: '$base500',
    svg: {
      color: '$base500',
    },
  },

  //data state open
  '&[data-state="open"]': {
    [`& ${ClosedTriggerContent}`]: {
      display: 'none',
    },

    [`& ${OpenTriggerContent}`]: {
      display: 'block',
    },

    svg: {
      transform: 'rotate(180deg)',
      transition: 'transform 300ms ease-in-out',
    },
  },

  '&[data-state="closed"]': {
    svg: {
      transform: 'rotate(360deg)',
      transition: 'transform 300ms ease-in-out',
    },
  },
});

export const SaleDetailsItem = styled(Item, {
  overflow: 'hidden',
  outline: 'none',
  borderRadius: '0 0 12px 12px',
});

export const StyledSkeleton = styled(Skeleton, {
  width: 'calc(100vw - 32px)',
  height: 200,
  marginX: 'auto',
  minWidth: '343px',
  borderRadius: 8,
  '@tablet': {
    marginTop: 12,
  },

  '@laptop': {
    marginTop: 32,
    maxWidth: 393,
    width: 393,
  },

  variants: {
    leftNav: {
      true: {
        width: '100%',
      },
    },
  },
});

export const TableWrapper = styled('div', {
  padding: '8px 16px 8px',
});

export const Table = styled('table', {
  width: '100%',
  marginTop: 8,
});

export const TableHeader = styled('thead', {
  td: {
    textTransform: 'uppercase ',
    base: 'S',
    opacity: '0.5',
    fontWeight: 400,
    paddingBottom: 14,

    '&:not(:first-child)': {
      textAlign: 'right',
    },
  },
});

export const Row = styled('tr', {
  width: '100%',
  td: {
    paddingBottom: 8,
  },

  variants: {
    lastRow: {
      true: {
        borderBottom: '1px solid $base200',
        td: {
          paddingBottom: 16,
        },
      },
    },
  },
});

export const RegularCell = styled('td', {
  textTransform: 'uppercase ',
  base: 'M',
  opacity: '0.5',
  fontWeight: 400,
  width: 94,

  '&:not(:first-child)': {
    textAlign: 'right',
  },
});

export const BoldCell = styled('td', {
  display: 'flex',
  gap: 4,
  alignItems: 'center',
  base: 'M',
  fontWeight: 500,
  textAlign: 'left',

  h4: {
    textTransform: 'capitalize',
  },
});

export const TotalRow = styled('tr', {
  width: '100%',
  td: {
    paddingTop: 14,
    base: 'M',
    textTransform: 'capitalize',
    fontWeight: 600,

    '&:not(:first-child)': {
      textAlign: 'right',
    },
  },
});

export const TextLabel = styled('div', {
  display: 'flex',
  alignItems: 'center',
  base: 'M',
  fontWeight: 500,
  gap: 4,
});

export const TextValue = styled('div', {
  base: 'M',
  fontWeight: 600,

  variants: {
    transform: {
      capitalize: {
        textTransform: 'capitalize',
      },

      uppercase: {
        textTransform: 'uppercase',
      },
    },
    size: {
      XL: {
        base: 'XL',
        fontWeight: 600,
      },
    },
  },
});

export const SaleDetailsHeading = () => (
  <TableHeader>
    <tr>
      <td>Details</td>
      <td>Collected</td>
      <td>Raised</td>
    </tr>
  </TableHeader>
);
